import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { bookingSearchTypes } from "@nexxt/contracts/booking";
import { catalogSearchTypes } from "@nexxt/contracts/catalog";
import { partySearchTypes } from "@nexxt/contracts/party";
import { procurementSearchTypes } from "@nexxt/contracts/procurement";
import { referenceSearchTypes } from "@nexxt/contracts/reference";
import { salesSearchTypes } from "@nexxt/contracts/sales";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";
import { type SearchDomain, type SearchTypeName, searchTypes } from "./search-types";

// Server state of the search (pattern.md §10, ADR-117): one request per domain, which answers one page for each type
// it is asked; the pages after the first are asked for one type at a time.

type HitOf<R extends { groups: { items: unknown[] }[] }> = R["groups"][number]["items"][number];

export type SearchHit =
  | HitOf<InferResponseType<typeof api.catalog.search.$get, 200>>
  | HitOf<InferResponseType<typeof api.procurement.search.$get, 200>>
  | HitOf<InferResponseType<typeof api.sales.search.$get, 200>>
  | HitOf<InferResponseType<typeof api.party.search.$get, 200>>
  | HitOf<InferResponseType<typeof api.booking.search.$get, 200>>
  | HitOf<InferResponseType<typeof api.reference.search.$get, 200>>;

/** A page of one type, best first. */
export interface SearchGroup {
  type: SearchTypeName;
  page: number;
  pageSize: number;
  hasMore: boolean;
  items: SearchHit[];
}

interface Ask {
  q: string;
  /** One type of the domain, or all those the user can read. */
  type: SearchTypeName | undefined;
  page: number;
  pageSize: number;
}

/** The type asked, if it is one of `types`: what the query string of that domain accepts. */
const typeIn = <T extends string>(types: readonly T[], type: string | undefined): T | undefined =>
  types.find((t) => t === type);

const queryOf = <T extends string>(types: readonly T[], { q, type, page, pageSize }: Ask) => {
  const asked = typeIn(types, type);
  return { q, ...(asked && { type: asked }), page: String(page), pageSize: String(pageSize) };
};

const ask: Record<SearchDomain, (input: Ask, signal: AbortSignal) => Promise<{ groups: SearchGroup[] }>> = {
  catalog: (input, signal) =>
    unwrap(api.catalog.search.$get({ query: queryOf(catalogSearchTypes, input) }, { init: { signal } })),
  procurement: (input, signal) =>
    unwrap(api.procurement.search.$get({ query: queryOf(procurementSearchTypes, input) }, { init: { signal } })),
  sales: (input, signal) =>
    unwrap(api.sales.search.$get({ query: queryOf(salesSearchTypes, input) }, { init: { signal } })),
  party: (input, signal) =>
    unwrap(api.party.search.$get({ query: queryOf(partySearchTypes, input) }, { init: { signal } })),
  booking: (input, signal) =>
    unwrap(api.booking.search.$get({ query: queryOf(bookingSearchTypes, input) }, { init: { signal } })),
  reference: (input, signal) =>
    unwrap(api.reference.search.$get({ query: queryOf(referenceSearchTypes, input) }, { init: { signal } })),
};

const keys = { all: ["search"] as const };

export const searchQueries = {
  ...keys,

  /** The first page of each type of a domain (only `type`, when there is one). */
  domain: (domain: SearchDomain, q: string, type: SearchTypeName | undefined, pageSize: number) =>
    queryOptions({
      queryKey: [...keys.all, domain, { q, type, pageSize }],
      queryFn: ({ signal }) => ask[domain]({ q, type, page: 1, pageSize }, signal),
      staleTime: 10_000,
    }),

  /** The pages of one type after the first, which the domain request already brought. */
  more: (type: SearchTypeName, q: string, pageSize: number) =>
    infiniteQueryOptions({
      queryKey: [...keys.all, "more", type, { q, pageSize }],
      queryFn: async ({ pageParam, signal }): Promise<SearchGroup> => {
        const { groups } = await ask[searchTypes[type].domain]({ q, type, page: pageParam, pageSize }, signal);
        return groups.find((g) => g.type === type) ?? { type, page: pageParam, pageSize, hasMore: false, items: [] };
      },
      initialPageParam: 2,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
      staleTime: 10_000,
    }),
};
